<template>
  <div class="constraints-edit-modal">
    <div class="head">
      <div class="field-settings-title-wrap">
        <h3>Field Settings & Constraints</h3>

        <div class="field-settings-current-field" :title="currentEditingFieldTitle">
          Editing field: <strong>{{ currentEditingFieldTitle }}</strong>
          <span class="field-settings-type-inline">
            Type of field: <strong>{{ currentTypeLabel }}</strong>
          </span>
        </div>
      </div>
    </div>

    <div class="dialog-tabs">
      <button
        type="button"
        class="dialog-tab"
        :class="{ active: activeTab === 'basic' }"
        @click="activeTab = 'basic'"
      >
        Basic
      </button>
      <button
        type="button"
        class="dialog-tab"
        :class="{ active: activeTab === 'advanced' }"
        @click="activeTab = 'advanced'"
      >
        Advanced
      </button>
    </div>

    <!-- ========================= BASIC TAB ========================= -->
    <template v-if="activeTab === 'basic'">
      <section class="group">
        <div class="row" v-if="isSlider">
          <label>Control type</label>
          <div class="choice-row">
            <label class="chk">
              <input type="radio" value="slider" v-model="local.mode" />
              Slider
            </label>
            <label class="chk">
              <input type="radio" value="linear" v-model="local.mode" />
              Likert scale
            </label>
          </div>
        </div>
        <div class="row" v-if="!hideBasicTypeControls && !isTable">
          <label>Field type</label>
          <select v-model="localType" @change="onFieldTypeChange" :disabled="!allowTypeChange">
            <option
              v-for="opt in FIELD_TYPE_OPTIONS"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div
          v-if="conversionReport && (conversionReport.lossy || (conversionReport.warnings && conversionReport.warnings.length))"
          class="row conversion-warning"
        >
          <label>Conversion warning</label>
          <div class="conversion-warning-box">
            <template v-if="conversionReport.warnings && conversionReport.warnings.length">
              <ul>
                <li v-for="(warn, i) in conversionReport.warnings" :key="i">
                  {{ warn }}
                </li>
              </ul>
            </template>
            <template v-else>
              <div>
                Changing this field type may discard incompatible settings or values.
              </div>
            </template>

            <label v-if="conversionReport.lossy" class="chk conversion-confirm">
              <input type="checkbox" v-model="lossyConversionConfirmed" />
              I understand and want to continue
            </label>
          </div>
        </div>

        <div class="row">
          <label class="chk">
            <input type="checkbox" v-model="local.required" />
            Required
          </label>
          <label class="chk">
            <input type="checkbox" v-model="local.readonly" />
            Readonly
          </label>
        </div>

        <div class="row" v-if="!isCheckbox && !isTable && !(isSlider && local.mode === 'slider') && !isFile">
          <label>Placeholder</label>
          <input type="text" v-model="local.placeholder" placeholder="Shown when empty" />
        </div>

        <div class="row">
          <label>Help text</label>
          <BaseTextarea
            id="field-help-text"
            label=""
            v-model="local.helpText"
            placeholder="Shown below the control"
            :rows="2"
          />
        </div>

        <div class="row" v-if="isTime">
          <label>Hour format</label>
          <select v-model="local.hourCycle">
            <option value="24">24-hour</option>
            <option value="12">12-hour (AM/PM)</option>
          </select>
        </div>

        <div class="row" v-if="!isDate && !isTable && !(isSlider && local.mode === 'slider') && !isFile">
          <label>Default value</label>

          <select v-if="isChoice && !local.allowMultiple" v-model="local.defaultValue">
            <option value="">(none)</option>
            <option v-for="(opt, i) in localOptions" :key="i" :value="opt">{{ opt }}</option>
          </select>

          <div v-else-if="isRadio && local.allowMultiple" class="chips">
            <div class="chip-input-row">
              <input
                type="text"
                v-model="chipInput"
                placeholder="Type option and press Enter"
                @keydown.enter.prevent="addChip"
                list="radio-options"
              />
              <datalist id="radio-options">
                <option v-for="(opt, i) in localOptions" :key="i" :value="opt" />
              </datalist>
            </div>
            <div class="chip-group">
              <span
                v-for="(val, i) in (Array.isArray(local.defaultValue) ? local.defaultValue : [])"
                :key="i"
                class="chip"
              >
                {{ val }}
                <button class="chip-x" @click.prevent="removeChip(i)">×</button>
              </span>
            </div>
          </div>

          <input
            v-else-if="isTextLike"
            type="text"
            v-model="local.defaultValue"
            placeholder="Default value"
          />

          <input
            v-else-if="isNumber"
            type="number"
            v-model.number="local.defaultValue"
            placeholder="Default number"
          />

          <FieldTime
            v-else-if="isTime"
            v-model="local.defaultValue"
            :hourCycle="local.hourCycle || '24'"
            :readonly="false"
            :disabled="false"
          />

          <label class="chk" v-else-if="isCheckbox">
            <input type="checkbox" v-model="local.defaultValue" />
            Checked by default
          </label>

          <input
            v-else
            type="text"
            v-model="local.defaultValue"
            placeholder="Default value"
          />

          <small v-if="defaultError && !isDate" class="err">{{ defaultError }}</small>
        </div>
      </section>

      <section class="group" v-if="isTextLike">
        <div class="row two">
          <div>
            <label>Min length</label>
            <input type="number" v-model.number="local.minLength" min="0" />
          </div>
          <div>
            <label>Max length</label>
            <input type="number" v-model.number="local.maxLength" min="0" />
          </div>
        </div>
        <div class="row">
          <label>Regex pattern</label>
          <input type="text" v-model="local.pattern" placeholder="e.g. ^[A-Za-z]+$" />
        </div>
        <div class="row">
          <label>Transform</label>
          <select v-model="local.transform">
            <option value="none">None</option>
            <option value="uppercase">Uppercase</option>
            <option value="lowercase">Lowercase</option>
            <option value="capitalize">Capitalize</option>
          </select>
        </div>
      </section>

      <section class="group" v-if="isNumber">
        <div class="row two">
          <div>
            <label>Value meaning</label>
            <select v-model="localMeasurementConcept" @change="onMeasurementConceptChanged">
              <option v-for="concept in measurementConceptOptions" :key="concept.value" :value="concept.value">
                {{ concept.label }}
              </option>
            </select>
            <small class="note">Explicit classification only; the field label is never guessed.</small>
          </div>
          <div v-if="localMeasurementConcept">
            <label>Unit</label>
            <select v-model="localMeasurementUnitMode" @change="onMeasurementUnitModeChanged">
              <option value="study_default">Study default ({{ inheritedMeasurementUnit }})</option>
              <option v-for="unit in measurementUnitOptions" :key="unit" :value="`field:${unit}`">
                Override: {{ unit }}
              </option>
            </select>
          </div>
        </div>

        <div class="row two">
          <div>
            <label>Min value</label>
            <input type="number" :value="num(local.min)" @input="setNum('min', $event)" />
          </div>
          <div>
            <label>Max value</label>
            <input type="number" :value="num(local.max)" @input="setNum('max', $event)" />
          </div>
        </div>

        <div class="row">
          <label>Step</label>
          <input
            type="number"
            :value="num(local.step)"
            @input="setNum('step', $event)"
            placeholder="Leave empty if not applicable"
          />
        </div>

        <div class="row">
          <label class="chk">
            <input type="checkbox" v-model="local.integerOnly" />
            Integer only
          </label>
        </div>

        <div class="row two">
          <div>
            <label>Min digits (integer part)</label>
            <input type="number" v-model.number="local.minDigits" min="0" />
          </div>
          <div>
            <label>Max digits (integer part)</label>
            <input type="number" v-model.number="local.maxDigits" min="0" />
          </div>
        </div>
        <div class="row note">
          <span>
            Digit limits apply to the integer part. In preview we enforce these when <b>Integer only</b> is checked.
            For values that need leading zeros, consider a <b>Text</b> field with a pattern.
          </span>
        </div>
      </section>

      <section class="group" v-if="isTime">
        <div class="row two">
          <div>
            <label>Min time</label>
            <FieldTime
              v-model="local.minTime"
              :hourCycle="local.hourCycle || '24'"
              :readonly="false"
              :disabled="false"
              :placeholder="(local.hourCycle === '12' ? 'hh:mm AM/PM' : 'HH:mm')"
            />
          </div>
          <div>
            <label>Max time</label>
            <FieldTime
              v-model="local.maxTime"
              :hourCycle="local.hourCycle || '24'"
              :readonly="false"
              :disabled="false"
              :placeholder="(local.hourCycle === '12' ? 'hh:mm AM/PM' : 'HH:mm')"
            />
          </div>
        </div>
      </section>

      <section class="group" v-if="isDate">
        <div class="row">
          <label>Date format</label>
          <select v-model="local.dateFormat">
            <option v-for="fmt in DATE_FORMATS" :key="fmt" :value="fmt">{{ fmt }}</option>
          </select>
        </div>

        <div class="row two">
          <div>
            <label>Min date</label>
            <DateFormatPicker
              v-model="local.minDate"
              :format="local.dateFormat"
              :placeholder="local.dateFormat"
            />
          </div>
          <div>
            <label>Max date</label>
            <DateFormatPicker
              v-model="local.maxDate"
              :format="local.dateFormat"
              :placeholder="local.dateFormat"
            />
          </div>
        </div>

        <div class="row">
          <label>Default date</label>
          <DateFormatPicker
            v-model="local.defaultValue"
            :format="local.dateFormat"
            :placeholder="local.dateFormat"
            :min-date="local.minDate || null"
            :max-date="local.maxDate || null"
          />
          <small v-if="defaultError && isDate" class="err">{{ defaultError }}</small>
        </div>
      </section>

      <section class="group" v-if="isFile">
        <div class="row">
          <label>Allowed formats</label>
          <input
            type="text"
            v-model="allowedFormatsText"
            placeholder="Examples: .pdf, .csv, .tsv, image/*, application/zip"
          />
          <small class="note">
            Comma-separated list. Client validates on upload; re-validate server-side if needed.
          </small>
        </div>

        <div class="row two">
          <div>
            <label>Max size (MB)</label>
            <input type="number" min="1" step="1" v-model.number="local.maxSizeMB" />
          </div>
          <div>
            <label>Storage behavior</label>
            <select v-model="local.storagePreference">
              <option value="local">Local storage (upload)</option>
              <option value="url">Link via URL</option>
            </select>
            <small class="note">This decides which input appears in the form.</small>
          </div>
        </div>

        <div class="row">
          <label class="chk">
            <input type="checkbox" v-model="local.allowMultipleFiles" />
            Allow multiple files
          </label>
        </div>

        <div class="row">
          <label>Modalities (BIDS)</label>
          <div class="chips">
            <div class="chip-group">
              <label
                v-for="mod in allModalities"
                :key="mod"
                class="chip selectable"
              >
                <input type="checkbox" :value="mod" v-model="local.modalities" />
                {{ mod }}
                <button
                  v-if="!builtInSet.has(mod)"
                  class="chip-x"
                  @click.prevent="removeCustomModByName(mod)"
                  title="Remove custom modality"
                >×</button>
              </label>
            </div>

            <div class="chip-input-row add-mod-row">
              <input
                type="text"
                v-model="customMod"
                placeholder="Custom modality (Enter or +)"
                @keydown.enter.prevent="addCustomMod"
              />
              <button type="button" class="btn-option add-mod-btn" @click="addCustomMod">+</button>
            </div>

            <small class="note">Used to help organize and name files in BIDS folders.</small>
          </div>
        </div>
      </section>

      <section class="group" v-if="isSlider && local.mode === 'slider'">
        <div class="row two">
          <div>
            <label>Min</label>
            <input type="number" :value="num(local.min)" @input="setNum('min', $event)" :disabled="local.percent" />
          </div>
          <div>
            <label>Max</label>
            <input type="number" :value="num(local.max)" @input="setNum('max', $event)" :disabled="local.percent" />
          </div>
        </div>

        <div class="row two">
          <div>
            <label>Step</label>
            <input
              type="number"
              min="0.000001"
              step="0.000001"
              :value="num(local.step)"
              @input="setNum('step', $event)"
            />
          </div>
          <div>
            <label class="chk">
              <input type="checkbox" v-model="local.percent" />
              Show as percentage (1–100)
            </label>
          </div>
        </div>

        <div class="row note">
          <span>No default selection for sliders. Clicking the track jumps to the nearest step.</span>
        </div>

        <div class="row">
          <label>Step labels</label>
          <div class="options-scroll">
            <div class="opt-row">
              <span class="opt-index">#</span>
              <input
                type="number"
                v-model.number="markEditValue"
                :min="useMin"
                :max="useMax"
                :step="useStep"
                placeholder="Step value"
              />
            </div>
            <div class="opt-row">
              <span class="opt-index">↳</span>
              <input
                type="text"
                v-model="markEditLabel"
                placeholder="Label"
                @keydown.enter.prevent="addOrUpdateMark('slider')"
              />
              <button class="icon-btn" title="Add/Update" @click.prevent="addOrUpdateMark('slider')">✓</button>
            </div>

            <div class="opt-row" v-for="(m, i) in marksSorted" :key="'s' + i">
              <span class="opt-index">{{ m.value }}</span>
              <input type="text" :value="m.label" disabled />
              <button class="icon-btn" title="Delete" @click.prevent="removeMark(m.value)">✕</button>
            </div>
          </div>
          <small class="note">We only store labels you add; no snap behavior.</small>
        </div>
      </section>

      <section class="group" v-if="isSlider && local.mode === 'linear'">
        <div class="row two">
          <div>
            <label>Min</label>
            <input type="number" :value="num(local.min)" @input="setNum('min', $event)" />
          </div>
          <div>
            <label>Max</label>
            <input type="number" :value="num(local.max)" @input="setNum('max', $event)" />
          </div>
        </div>
        <div class="row two">
          <div>
            <label>Left label</label>
            <input type="text" v-model="local.leftLabel" placeholder="e.g., Not happy" />
          </div>
          <div>
            <label>Right label</label>
            <input type="text" v-model="local.rightLabel" placeholder="e.g., Very happy" />
          </div>
        </div>
        <div class="row note" v-if="linearCount > LINEAR_MAX">
          <span class="err">Too many points ({{ linearCount }}). Limit is {{ LINEAR_MAX }} to avoid clutter.</span>
        </div>
        <div class="row note">
          <span>Likert scale shows only endpoints (left/right). No step labels.</span>
        </div>
      </section>

      <section class="group" v-if="isChoice">
        <div class="row">
          <label class="chk" v-if="isRadio">
            <input type="checkbox" v-model="local.allowMultiple" />
            Allow multiple selections
          </label>
          <label class="chk">
            <input type="checkbox" v-model="local.allowOther" />
            Include “Other” with a free-text answer
          </label>
        </div>

        <div class="row two">
          <div>
            <label>{{ local.allowOther ? 'Number of standard options' : 'Number of options' }}</label>
            <input type="number" min="1" v-model.number="optionsCount" @input="syncOptionsCount" />
          </div>
          <div>
            <label>Quick add</label>
            <div class="quick">
              <button type="button" class="btn-option" @click="addOption()">+ Add</button>
              <button type="button" class="btn-option" @click="removeLastOption()" :disabled="localOptions.length <= 1">− Remove</button>
              <button type="button" class="btn-option sort-option-btn" @click="sortChoiceOptions"
              :disabled="localOptions.length <= 1"
              :title="optionSortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'">
              {{ optionSortDirection === "asc" ? "↑ Sort" : "↓ Sort" }}
  </button>
            </div>
          </div>
        </div>

        <div class="options-scroll">
          <div
            class="opt-row"
            :class="{ 'has-exclusive-control': isRadio && local.allowMultiple }"
            v-for="({ opt, idx }, displayIndex) in standardOptionRows"
            :key="idx"
          >
            <span class="opt-index">{{ displayIndex + 1 }}.</span>
            <input
              type="text"
              :value="localOptions[idx]"
              placeholder="Option label"
              @input="renameOption(idx, $event.target.value)"
            />
            <label
              v-if="isRadio && local.allowMultiple"
              class="chk dominant-option"
              title="Selecting this option clears every other selection"
            >
              <input
                type="checkbox"
                :checked="isDominantOption(opt)"
                @change="setDominantOption(opt, $event.target.checked)"
              />
              Dominant
            </label>
            <div class="option-actions">
              <button
                type="button"
                class="icon-btn"
                title="Move up"
                :aria-label="`Move option ${idx + 1} up`"
                :disabled="idx === 0"
                @click.prevent="moveOption(idx, -1)"
              >↑</button>
              <button
                type="button"
                class="icon-btn"
                title="Move down"
                :aria-label="`Move option ${idx + 1} down`"
                :disabled="idx === localOptions.length - 1"
                @click.prevent="moveOption(idx, 1)"
              >↓</button>
              <button
                type="button"
                class="icon-btn"
                title="Delete"
                :aria-label="`Delete option ${idx + 1}`"
                :disabled="localOptions.length <= 1"
                @click.prevent="deleteOption(idx)"
              >✕</button>
            </div>
          </div>
          <div v-if="local.allowOther" class="opt-row">
            <span class="opt-index">{{ standardOptionRows.length + 1 }}.</span>
            <input type="text" value="Other" readonly aria-label="Other option (automatic)" />
            <span>Free text · added automatically</span>
          </div>
        </div>
        <div class="row note" v-if="isRadio && local.allowMultiple">
          <span>Dominant options cannot be combined with any other selection.</span>
        </div>
        <div class="row note" v-if="isSelect">
          <span>Dropdowns are single-select in this builder.</span>
        </div>
      </section>

      <section class="group" v-if="isCheckbox">
        <div class="row note">
          <span>Checkbox has no placeholder. Use help text for guidance.</span>
        </div>
      </section>
    </template>

    <!-- ========================= ADVANCED TAB ========================= -->
    <template v-else>
      <section class="group">
        <div class="row two">
          <div>
            <label>When conditions match</label>
            <select v-model="local.visibilityLogic.action">
              <option value="show">Show this field</option>
              <option value="hide">Hide this field</option>
            </select>
          </div>
          <div>
            <label>Condition mode</label>
            <select v-model="local.visibilityLogic.match">
              <option value="all">All conditions must match (AND)</option>
              <option value="any">Any one condition may match (OR)</option>
            </select>
          </div>
        </div>

        <div class="row note">
          <span>Configure this field’s visibility based on other fields.</span>
        </div>

        <div class="logic-rules">
          <div
            class="logic-rule-card"
            v-for="(rule, idx) in local.visibilityLogic.rules"
            :key="rule.id"
          >
            <div class="logic-rule-top">
              <strong>Condition {{ idx + 1 }}</strong>
              <div class="logic-rule-top-actions">
                <button class="btn-mini" @click.prevent="clearLogicRule(rule)">Clear</button>
                <button class="icon-btn" @click.prevent="removeLogicRule(idx)">✕</button>
              </div>
            </div>

            <div class="row two">
              <div>
                <label>Source section</label>
                <select v-model="rule._sectionKey" @change="onSourceSectionChanged(rule)">
                  <option value="">Select section…</option>
                  <option
                    v-for="section in availableSourceSections"
                    :key="section.key"
                    :value="section.key"
                  >
                    {{ section.title }}
                  </option>
                </select>
              </div>

              <div>
                <label>Source field</label>
                <select
                  v-model="rule.sourceFieldKey"
                  @change="onSourceFieldChanged(rule)"
                  :disabled="!rule._sectionKey"
                >
                  <option value="">Select field…</option>
                  <option
                    v-for="f in fieldsForSelectedSection(rule)"
                    :key="f.key"
                    :value="f.key"
                  >
                    {{ f.label }}
                  </option>
                </select>
              </div>
            </div>

            <div class="row" v-if="rule.sourceFieldKey">
              <label>Operator</label>
              <select v-model="rule.operator" @change="onOperatorChanged(rule)">
                <option
                  v-for="op in availableOperatorsForRule(rule)"
                  :key="op.value"
                  :value="op.value"
                >
                  {{ op.label }}
                </option>
              </select>
            </div>

            <template v-if="needsNoValue(rule.operator)">
              <div class="row note">
                <span>No compare value needed for this operator.</span>
              </div>
            </template>

            <template v-else-if="rule.operator === 'between'">
              <div class="row two">
                <div>
                  <label>From</label>

                  <input
                    v-if="ruleSourceType(rule) === 'number' || ruleSourceType(rule) === 'slider'"
                    type="number"
                    v-model.number="rule.value"
                  />

                  <FieldTime
                    v-else-if="ruleSourceType(rule) === 'time'"
                    v-model="rule.value"
                    :hourCycle="sourceHourCycle(rule)"
                    :readonly="false"
                    :disabled="false"
                  />

                  <DateFormatPicker
                    v-else-if="ruleSourceType(rule) === 'date'"
                    v-model="rule.value"
                    :format="sourceDateFormat(rule)"
                    :placeholder="sourceDateFormat(rule)"
                  />

                  <input
                    v-else
                    type="text"
                    v-model="rule.value"
                  />
                </div>

                <div>
                  <label>To</label>

                  <input
                    v-if="ruleSourceType(rule) === 'number' || ruleSourceType(rule) === 'slider'"
                    type="number"
                    v-model.number="rule.valueTo"
                  />

                  <FieldTime
                    v-else-if="ruleSourceType(rule) === 'time'"
                    v-model="rule.valueTo"
                    :hourCycle="sourceHourCycle(rule)"
                    :readonly="false"
                    :disabled="false"
                  />

                  <DateFormatPicker
                    v-else-if="ruleSourceType(rule) === 'date'"
                    v-model="rule.valueTo"
                    :format="sourceDateFormat(rule)"
                    :placeholder="sourceDateFormat(rule)"
                  />

                  <input
                    v-else
                    type="text"
                    v-model="rule.valueTo"
                  />
                </div>
              </div>
            </template>

            <template v-else-if="ruleSourceIsChoiceSingle(rule)">
              <div class="row">
                <label>Compare value</label>
                <select v-model="rule.value">
                  <option value="">Select…</option>
                  <option
                    v-for="opt in sourceOptions(rule)"
                    :key="opt"
                    :value="opt"
                  >
                    {{ opt }}
                  </option>
                </select>
                <small class="note">Options from selected source field.</small>
              </div>
            </template>

            <template v-else-if="ruleSourceIsChoiceMulti(rule)">
              <div class="row">
                <label>Compare value(s)</label>
                <div class="chips">
                  <div class="chip-input-row">
                    <input
                      type="text"
                      v-model="rule._chipInput"
                      placeholder="Type option and press Enter"
                      @keydown.enter.prevent="addRuleChip(rule)"
                      :list="`logic-options-${rule.id}`"
                    />
                    <datalist :id="`logic-options-${rule.id}`">
                      <option v-for="opt in sourceOptions(rule)" :key="opt" :value="opt" />
                    </datalist>
                  </div>
                  <div class="chip-group">
                    <span
                      v-for="(val, i) in (Array.isArray(rule.value) ? rule.value : [])"
                      :key="i"
                      class="chip"
                    >
                      {{ val }}
                      <button class="chip-x" @click.prevent="removeRuleChip(rule, i)">×</button>
                    </span>
                  </div>
                </div>
              </div>
            </template>

            <template v-else-if="ruleSourceType(rule) === 'checkbox'">
              <div class="row">
                <label>Compare value</label>
                <select v-model="rule.value">
                  <option :value="true">Checked</option>
                  <option :value="false">Unchecked</option>
                </select>
              </div>
            </template>

            <template v-else>
              <div class="row">
                <label>Compare value</label>

                <input
                  v-if="ruleSourceType(rule) === 'number' || ruleSourceType(rule) === 'slider'"
                  type="number"
                  v-model.number="rule.value"
                />

                <FieldTime
                  v-else-if="ruleSourceType(rule) === 'time'"
                  v-model="rule.value"
                  :hourCycle="sourceHourCycle(rule)"
                  :readonly="false"
                  :disabled="false"
                />

                <DateFormatPicker
                  v-else-if="ruleSourceType(rule) === 'date'"
                  v-model="rule.value"
                  :format="sourceDateFormat(rule)"
                  :placeholder="sourceDateFormat(rule)"
                />

                <input
                  v-else
                  type="text"
                  v-model="rule.value"
                  placeholder="Enter compare value"
                />
              </div>
            </template>

            <div class="row note">
              <span>{{ logicSummary(rule) }}</span>
            </div>
          </div>
        </div>

        <div class="row">
          <button type="button" class="btn-option" @click="addLogicRule">
            + Add condition
          </button>
        </div>
      </section>

      <section class="group">
        <div class="row">
          <label class="chk">
            <input type="checkbox" v-model="local.popupLogic.enabled" />
            Enable pop-up message
          </label>
        </div>

        <template v-if="local.popupLogic.enabled">
          <div class="row note">
            <span>
              Configure a reminder message based on this field’s own value. The source field is the current field:
              <b>{{ currentFieldLabel || fieldDefinition?.label || fieldDefinition?.name || "Current field" }}</b>.
            </span>
          </div>

          <div class="row">
            <label>Operator</label>
            <select v-model="local.popupLogic.operator" @change="onPopupOperatorChanged">
              <option
                v-for="op in availablePopupOperatorsForCurrentField()"
                :key="op.value"
                :value="op.value"
              >
                {{ op.label }}
              </option>
            </select>
          </div>

          <template v-if="popupNeedsNoValue(local.popupLogic.operator)">
            <div class="row note">
              <span>No compare value needed for this operator.</span>
            </div>
          </template>

          <template v-else-if="local.popupLogic.operator === 'between'">
            <div class="row two">
              <div>
                <label>From</label>

                <input
                  v-if="popupSourceType() === 'number' || popupSourceType() === 'slider'"
                  type="number"
                  v-model.number="local.popupLogic.value"
                />

                <FieldTime
                  v-else-if="popupSourceType() === 'time'"
                  v-model="local.popupLogic.value"
                  :hourCycle="local.hourCycle || '24'"
                  :readonly="false"
                  :disabled="false"
                />

                <DateFormatPicker
                  v-else-if="popupSourceType() === 'date'"
                  v-model="local.popupLogic.value"
                  :format="local.dateFormat || 'dd.MM.yyyy'"
                  :placeholder="local.dateFormat || 'dd.MM.yyyy'"
                />

                <input
                  v-else
                  type="text"
                  v-model="local.popupLogic.value"
                />
              </div>

              <div>
                <label>To</label>

                <input
                  v-if="popupSourceType() === 'number' || popupSourceType() === 'slider'"
                  type="number"
                  v-model.number="local.popupLogic.valueTo"
                />

                <FieldTime
                  v-else-if="popupSourceType() === 'time'"
                  v-model="local.popupLogic.valueTo"
                  :hourCycle="local.hourCycle || '24'"
                  :readonly="false"
                  :disabled="false"
                />

                <DateFormatPicker
                  v-else-if="popupSourceType() === 'date'"
                  v-model="local.popupLogic.valueTo"
                  :format="local.dateFormat || 'dd.MM.yyyy'"
                  :placeholder="local.dateFormat || 'dd.MM.yyyy'"
                />

                <input
                  v-else
                  type="text"
                  v-model="local.popupLogic.valueTo"
                />
              </div>
            </div>
          </template>

          <template v-else-if="popupSourceIsChoiceSingle()">
            <div class="row">
              <label>Compare value</label>
              <select v-model="local.popupLogic.value">
                <option value="">Select…</option>
                <option
                  v-for="opt in localOptions"
                  :key="opt"
                  :value="opt"
                >
                  {{ opt }}
                </option>
              </select>
              <small class="note">Options from this field.</small>
            </div>
          </template>

          <template v-else-if="popupSourceIsChoiceMulti()">
            <div class="row">
              <label>Compare value(s)</label>
              <div class="chips">
                <div class="chip-input-row">
                  <input
                    type="text"
                    v-model="local.popupLogic._chipInput"
                    placeholder="Type option and press Enter"
                    @keydown.enter.prevent="addPopupChip"
                    list="popup-current-field-options"
                  />
                  <datalist id="popup-current-field-options">
                    <option v-for="opt in localOptions" :key="opt" :value="opt" />
                  </datalist>
                </div>
                <div class="chip-group">
                  <span
                    v-for="(val, i) in (Array.isArray(local.popupLogic.value) ? local.popupLogic.value : [])"
                    :key="i"
                    class="chip"
                  >
                    {{ val }}
                    <button class="chip-x" @click.prevent="removePopupChip(i)">×</button>
                  </span>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="popupSourceType() === 'checkbox'">
            <div class="row">
              <label>Compare value</label>
              <select v-model="local.popupLogic.value">
                <option :value="true">Checked</option>
                <option :value="false">Unchecked</option>
              </select>
            </div>
          </template>

          <template v-else>
            <div class="row">
              <label>Compare value</label>

              <input
                v-if="popupSourceType() === 'number' || popupSourceType() === 'slider'"
                type="number"
                v-model.number="local.popupLogic.value"
              />

              <FieldTime
                v-else-if="popupSourceType() === 'time'"
                v-model="local.popupLogic.value"
                :hourCycle="local.hourCycle || '24'"
                :readonly="false"
                :disabled="false"
              />

              <DateFormatPicker
                v-else-if="popupSourceType() === 'date'"
                v-model="local.popupLogic.value"
                :format="local.dateFormat || 'dd.MM.yyyy'"
                :placeholder="local.dateFormat || 'dd.MM.yyyy'"
              />

              <input
                v-else
                type="text"
                v-model="local.popupLogic.value"
                placeholder="Enter compare value"
              />
            </div>
          </template>

          <div class="row">
            <label>Pop-up message</label>
            <BaseTextarea
              id="field-popup-message"
              label=""
              v-model="local.popupLogic.message"
              placeholder="Example: Please also document this medication in the Medication section."
              :rows="3"
            />
          </div>
          <div class="row">
              <label class="chk">
                <input
                  type="checkbox"
                  v-model="local.popupLogic.showInTargetField"
                  @change="onPopupTargetToggle"
                />
                Do you want the message to be shown in target field too?
              </label>
            </div>

            <template v-if="local.popupLogic.showInTargetField">
              <div class="row two">
                <div>
                  <label>Target section</label>
                  <select
                    v-model="local.popupLogic._targetSectionKey"
                    @change="onPopupTargetSectionChanged"
                  >
                    <option value="">Select section…</option>
                    <option
                      v-for="section in availableTargetSections"
                      :key="section.key"
                      :value="section.key"
                    >
                      {{ section.title }}
                    </option>
                  </select>
                </div>

                <div>
                  <label>Target field</label>
                  <select
                    v-model="local.popupLogic.targetFieldKey"
                    :disabled="!local.popupLogic._targetSectionKey"
                  >
                    <option value="">Select field…</option>
                    <option
                      v-for="f in fieldsForSelectedPopupTargetSection()"
                      :key="f.key"
                      :value="f.key"
                    >
                      {{ f.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="row note">
                <span>
                  The same reminder will be shown below the current source field and the selected target field.
                </span>
              </div>
            </template>
          <div class="row note">
            <span>{{ popupSummary() }}</span>
          </div>
        </template>
      </section>
    </template>

    <div class="modal-actions">
      <button class="btn-primary" @click="save" :disabled="isSaveDisabled">Save</button>
      <button class="btn-option" @click="clearToInitial">Clear</button>
      <button class="btn-option" @click="$emit('closeConstraintsDialog')">Cancel</button>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { normalizeConstraints, coerceDefaultForType } from "@/utils/constraints";
import {
  haveSameChoiceOptions,
  normalizeDominantOptions,
  normalizeMultiChoiceValue,
} from "@/utils/dominantChoice";
import {
  constraintEditSnapshot,
  constraintsForSave,
} from "@/utils/constraintEditState";
import {
  FIELD_TYPE_OPTIONS,
  getFieldTypeConversionReport,
  buildConvertedField,
} from "@/utils/fieldTypeConversion";
import DateFormatPicker from "@/components/DateFormatPicker.vue";
import FieldTime from "@/components/fields/FieldTime.vue";
import BaseTextarea from "@/components/forms/BaseTextarea.vue";
import {
  MEASUREMENT_CONCEPTS,
  measurementConcept,
  normalizeStudySettings,
} from "@/utils/studySettings";

const DATE_FORMATS = [
  "dd.MM.yyyy",
  "MM-dd-yyyy",
  "dd-MM-yyyy",
  "yyyy-MM-dd",
  "MM/yyyy",
  "MM-yyyy",
  "yyyy/MM",
  "yyyy-MM",
  "yyyy",
];

const BIDS_MODALITIES = Object.freeze([
  "MRI",
  "T1w",
  "T2w",
  "bold",
  "dwi",
  "FLAIR",
  "PD",
  "inplaneT2",
  "fmap",
  "fieldmap",
  "epi",
  "phasediff",
  "magnitude",
  "MEG",
  "EEG",
  "iEEG",
  "NIRS",
  "PET",
  "ASL",
  "beh",
  "events",
  "physio",
  "scans",
  "func",
  "anat",
  "fmap",
  "swi",
]);

function choiceOptionsFromField(field) {
  const direct = Array.isArray(field?.options) ? field.options : [];
  const fromConstraints = Array.isArray(field?.constraints?.options)
    ? field.constraints.options
    : [];

  return Array.from(
    new Set(
      [...direct, ...fromConstraints]
        .map((v) => String(v ?? "").trim())
        .filter(Boolean)
    )
  );
}

function buildInitialLocal(vm, constraintsForm, currentFieldType) {
  const base = constraintsForm || {};
  const type = (currentFieldType || "text").toLowerCase();

  const initialOptions = (Array.isArray(base.options) ? base.options : [])
    .filter(Boolean)
    .map(String);

  const defaultValue = (() => {
    if (type === "table") {
      return base.defaultValue;
    }

    if (type === "checkbox") {
      return (
        base.defaultValue === true ||
        base.defaultValue === "true" ||
        base.defaultValue === 1 ||
        base.defaultValue === "1"
      );
    }

    if (type === "radio" && base.allowMultiple) {
      if (Array.isArray(base.defaultValue)) {
        return base.defaultValue
          .map((v) => String(v ?? "").trim())
          .filter(Boolean);
      }

      if (
        base.defaultValue === "" ||
        base.defaultValue === null ||
        base.defaultValue === undefined
      ) {
        return [];
      }

      return [String(base.defaultValue).trim()].filter(Boolean);
    }

    if (base.defaultValue === null || base.defaultValue === undefined) {
      return "";
    }

    return base.defaultValue;
  })();

  const allowedFormats = Array.isArray(base.allowedFormats)
    ? base.allowedFormats.map(String).map((s) => s.trim()).filter(Boolean)
    : [];

  return {
    required: !!base.required,
    readonly: !!base.readonly,
    helpText: base.helpText || "",
    placeholder: type === "table" ? "" : (base.placeholder || ""),
    defaultValue,

    minLength: isFinite(base.minLength) ? Number(base.minLength) : undefined,
    maxLength: isFinite(base.maxLength) ? Number(base.maxLength) : undefined,
    pattern: base.pattern || "",
    transform: base.transform || "none",

    min: isFinite(base.min) ? Number(base.min) : type === "slider" ? 1 : undefined,
    max: isFinite(base.max) ? Number(base.max) : type === "slider" ? 5 : undefined,
    step: isFinite(base.step) ? Number(base.step) : type === "slider" ? 1 : undefined,
    integerOnly: !!base.integerOnly,
    minDigits: isFinite(base.minDigits) ? Number(base.minDigits) : undefined,
    maxDigits: isFinite(base.maxDigits) ? Number(base.maxDigits) : undefined,
    measurement:
      base.measurement && typeof base.measurement === "object"
        ? JSON.parse(JSON.stringify(base.measurement))
        : undefined,

    minTime: base.minTime || "",
    maxTime: base.maxTime || "",
    hourCycle: base.hourCycle || "24",

    minDate: base.minDate || "",
    maxDate: base.maxDate || "",
    dateFormat: base.dateFormat || "dd.MM.yyyy",

    allowMultiple: !!base.allowMultiple,
    allowOther: !!base.allowOther,
    dominantOptions: Array.isArray(base.dominantOptions)
      ? base.dominantOptions.map(String)
      : [],

    mode: base.mode === "linear" ? "linear" : "slider",
    percent: !!base.percent,

    leftLabel: base.leftLabel || "",
    rightLabel: base.rightLabel || "",

    marks: Array.isArray(base.marks)
      ? base.marks
          .map((m) => ({ value: Number(m?.value), label: String(m?.label ?? "") }))
          .filter((m) => Number.isFinite(m.value) && m.label)
      : base.marks && typeof base.marks === "object"
      ? Object.keys(base.marks)
          .map((k) => ({ value: Number(k), label: String(base.marks[k]) }))
          .filter((m) => Number.isFinite(m.value) && m.label)
      : [],

    allowedFormats,
    maxSizeMB:
      isFinite(base.maxSizeMB) && Number(base.maxSizeMB) > 0
        ? Number(base.maxSizeMB)
        : undefined,
    storagePreference: base.storagePreference === "url" ? "url" : "local",
    allowMultipleFiles: base.allowMultipleFiles === undefined ? true : !!base.allowMultipleFiles,
    modalities: Array.isArray(base.modalities)
      ? base.modalities.filter(Boolean).map(String)
      : [],

    visibilityLogic: vm.normalizeVisibilityLogic(base.visibilityLogic),
    popupLogic: vm.normalizePopupLogic(base.popupLogic, type, !!base.allowMultiple),
  };
}

export default {
  name: "FieldConstraintsDialog",
  components: { DateFormatPicker, FieldTime, BaseTextarea },
  props: {
    currentFieldType: { type: String, default: "text" },
    constraintsForm: { type: Object, default: () => ({}) },
    form: { type: Object, default: () => ({ sections: [] }) },
    currentFieldKey: { type: String, default: "" },
    currentFieldLabel: { type: String, default: "" },
    fieldDefinition: { type: Object, default: () => ({}) },
    allowTypeChange: { type: Boolean, default: true },
    hideBasicTypeControls: { type: Boolean, default: false },
    forceTypeLabel: { type: String, default: "" },
    studySettings: { type: Object, default: () => ({}) },
  },

  data() {
    const local = buildInitialLocal(this, this.constraintsForm, this.currentFieldType);

    const initialOptions = choiceOptionsFromField({
      ...(this.fieldDefinition || {}),
      constraints: this.constraintsForm || {},
      options: Array.isArray(this.fieldDefinition?.options)
        ? this.fieldDefinition.options
        : this.constraintsForm?.options,
    });

    const allowedFormats = Array.isArray(this.constraintsForm?.allowedFormats)
      ? this.constraintsForm.allowedFormats.map(String).map((s) => s.trim()).filter(Boolean)
      : [];

    return {
      activeTab: "basic",
      DATE_FORMATS,
      LINEAR_MAX: 10,
      BIDS_MODALITIES,
      customMod: "",
      allowedFormatsText: allowedFormats.join(", "),
      optionSortDirection: "asc",

      localType: (this.currentFieldType || "text").toLowerCase(),
      FIELD_TYPE_OPTIONS,
      conversionReport: null,
      lossyConversionConfirmed: false,

      local,
      localOptions: initialOptions.length ? initialOptions : ["Option 1"],
      optionsCount: Math.max(1, initialOptions.length || 1),
      initialConstraintEditSnapshot: "",
      initialConstraintOptions: initialOptions.length
        ? [...initialOptions]
        : ["Option 1"],

      chipInput: "",
      markEditValue: null,
      markEditLabel: "",
      localMeasurementConcept: String(local?.measurement?.concept || ""),
      localMeasurementUnitMode:
        local?.measurement?.unitSource === "field" && local?.measurement?.unit
          ? `field:${local.measurement.unit}`
          : "study_default",
    };
  },
  mounted() {
    this.syncRuleSectionKeys();
    this.$nextTick(() => this.captureInitialConstraintState());
  },

  computed: {
    measurementConceptOptions() {
      return MEASUREMENT_CONCEPTS;
    },
    measurementUnitOptions() {
      return measurementConcept(this.localMeasurementConcept).units || [];
    },
    inheritedMeasurementUnit() {
      const settings = normalizeStudySettings(this.studySettings);
      return settings.measurementDefaults[this.localMeasurementConcept] || this.measurementUnitOptions[0] || "not set";
    },
    standardOptionRows() {
      return this.localOptions.map((opt, idx) => ({ opt, idx }))
        .filter(({ opt }) => !this.local.allowOther || String(opt).trim().toLowerCase() !== 'other');
    },
    currentEditingFieldTitle() {
      return (
        this.currentFieldLabel ||
        this.fieldDefinition?.label ||
        this.fieldDefinition?.name ||
        this.fieldDefinition?.title ||
        "Current field"
      );
    },
    type() {
      return (this.localType || this.currentFieldType || "text").toLowerCase();
    },
    isTextLike() {
      return this.type === "text" || this.type === "textarea";
    },
    isNumber() {
      return this.type === "number";
    },
    isTime() {
      return this.type === "time";
    },
    isDate() {
      return this.type === "date";
    },
    isCheckbox() {
      return this.type === "checkbox";
    },
    isRadio() {
      return this.type === "radio";
    },
    isSelect() {
      return this.type === "select";
    },
    isChoice() {
      return this.isRadio || this.isSelect;
    },
    isSlider() {
      return this.type === "slider";
    },
    isFile() {
      return this.type === "file";
    },
    currentTypeLabel() {
      if (this.forceTypeLabel) return this.forceTypeLabel;
      return this.type.charAt(0).toUpperCase() + this.type.slice(1);
    },

    useMin() {
      return Number.isFinite(this.local.min) ? this.local.min : 1;
    },
    useMax() {
      return Number.isFinite(this.local.max) ? this.local.max : 5;
    },
    useStep() {
      return Number.isFinite(this.local.step) && this.local.step > 0 ? this.local.step : 1;
    },

    linearCount() {
      const min = Number.isFinite(+this.local.min) ? +this.local.min : 1;
      const max = Number.isFinite(+this.local.max) ? +this.local.max : 5;
      return Math.max(0, max - min + 1);
    },

    marksSorted() {
      return [...(this.local.marks || [])]
        .sort((a, b) => a.value - b.value)
        .filter((m, idx, arr) => idx === 0 || m.value !== arr[idx - 1].value);
    },

    defaultError() {
      if (!(this.local.required && this.local.readonly)) return "";

      const t = this.type;
      const dv = this.local.defaultValue;

      if (t === "radio") {
        const ok = this.local.allowMultiple
          ? Array.isArray(dv) && dv.length > 0
          : typeof dv === "string" && dv !== "";
        return ok ? "" : "Default value is required when the field is both Required and Readonly.";
      }

      if (t === "select") {
        const ok = typeof dv === "string" && dv !== "";
        return ok ? "" : "Default value is required when the field is both Required and Readonly.";
      }

      if (t === "checkbox") {
        const ok = !!dv;
        return ok ? "" : "Default value (Checked) is required when the field is both Required and Readonly.";
      }

      if (t === "number") {
        const ok = dv !== "" && dv !== null && dv !== undefined && !Number.isNaN(Number(dv));
        return ok ? "" : "Default value is required when the field is both Required and Readonly.";
      }

      return dv !== "" && dv !== null && dv !== undefined
        ? ""
        : "Default value is required when the field is both Required and Readonly.";
    },

    isSaveDisabled() {
      if (this.isSlider && this.local.mode === "linear") {
        return this.linearCount < 2 || this.linearCount > this.LINEAR_MAX;
      }
      if (!this.isDate && !(this.isSlider && this.local.mode === "slider") && this.local.required && this.local.readonly) {
        return !!this.defaultError;
      }
      return false;
    },

    builtInSet() {
      return new Set(BIDS_MODALITIES);
    },
    allModalities() {
      const extras = (this.local.modalities || []).filter(
        (m) => !!m && !this.builtInSet.has(m)
      );
      return [...BIDS_MODALITIES, ...Array.from(new Set(extras))];
    },

    availableSourceFields() {
      const currentKey = String(this.currentFieldKey || "");
      const out = [];

      (this.form?.sections || []).forEach((section, si) => {
        (section.fields || []).forEach((field, fi) => {
          const key = field._id || field.name || `section_${si}_field_${fi}`;
          if (String(key) === currentKey) return;

          const fieldType = String(field.type || "text").toLowerCase();
          if (fieldType === "table") return;

          const constraints = field.constraints || {};

          out.push({
              key: String(key),
              sectionKey: `section_${si}`,
              label: field.label || field.name || `Field ${fi + 1}`,
              sectionTitle: section.title || `Section ${si + 1}`,
              pathLabel: `${section.title || `Section ${si + 1}`}.${field.label || field.name || `Field ${fi + 1}`}`,
              type: field.type || "text",
              options: choiceOptionsFromField(field),
              allowMultiple: !!constraints.allowMultiple,
              dateFormat: constraints.dateFormat || "dd.MM.yyyy",
              hourCycle: constraints.hourCycle || "24",
            });
        });
      });

      return out;
    },
    availableSourceSections() {
      const groups = [];
      const seen = new Map();

      (this.availableSourceFields || []).forEach((f) => {
        const sectionKey = String(f.sectionKey || "");
        if (!seen.has(sectionKey)) {
          const entry = {
            key: sectionKey,
            title: f.sectionTitle || "Untitled Section",
            fields: [],
          };
          seen.set(sectionKey, entry);
          groups.push(entry);
        }
        seen.get(sectionKey).fields.push(f);
      });

      return groups;
    },
    availableTargetFields() {
      const currentKey = String(this.currentFieldKey || "");
      const out = [];

      (this.form?.sections || []).forEach((section, si) => {
        (section.fields || []).forEach((field, fi) => {
          const key = field._id || field.id || field.field_id || field.uid || field.key || field.name || `section_${si}_field_${fi}`;

          if (String(key) === currentKey) return;

          out.push({
            key: String(key),
            sectionKey: `section_${si}`,
            label: field.label || field.name || field.title || `Field ${fi + 1}`,
            sectionTitle: section.title || `Section ${si + 1}`,
            pathLabel: `${section.title || `Section ${si + 1}`}.${field.label || field.name || field.title || `Field ${fi + 1}`}`,
            type: field.type || "text",
          });
        });
      });

      return out;
    },

    availableTargetSections() {
      const groups = [];
      const seen = new Map();

      (this.availableTargetFields || []).forEach((f) => {
        const sectionKey = String(f.sectionKey || "");
        if (!seen.has(sectionKey)) {
          const entry = {
            key: sectionKey,
            title: f.sectionTitle || "Untitled Section",
            fields: [],
          };
          seen.set(sectionKey, entry);
          groups.push(entry);
        }
        seen.get(sectionKey).fields.push(f);
      });

      return groups;
    },
  },

  watch: {
    constraintsForm: {
      deep: true,
      handler(nv) {
        const nextLocal = buildInitialLocal(this, nv, this.currentFieldType);
        const allowedFormats = Array.isArray(nv?.allowedFormats)
          ? nv.allowedFormats.map(String).map((s) => s.trim()).filter(Boolean)
          : [];

        this.allowedFormatsText = allowedFormats.join(", ");
        this.local = nextLocal;
        this.syncMeasurementEditor(nextLocal.measurement);
        this.localType = (this.currentFieldType || "text").toLowerCase();
        this.conversionReport = null;
        this.lossyConversionConfirmed = false;

        const cleaned = choiceOptionsFromField({
          ...(this.fieldDefinition || {}),
          constraints: nv || {},
          options: Array.isArray(this.fieldDefinition?.options)
            ? this.fieldDefinition.options
            : nv?.options,
        });

        if (cleaned.length) {
          this.localOptions = cleaned;
          this.optionsCount = this.localOptions.length;
        }

        this.$nextTick(() => {
          this.syncRuleSectionKeys();
          this.captureInitialConstraintState();
        });
      },
    },

    "local.percent"(on) {
      if (this.isSlider && this.local.mode === "slider" && on) {
        this.local.min = 1;
        this.local.max = 100;
        if (!Number.isFinite(this.local.step) || this.local.step <= 0) this.local.step = 1;
        this.local.marks = (this.local.marks || []).filter((m) => m.value >= 1 && m.value <= 100);
      }
    },

    currentFieldType(val) {
      const t = (val || "").toLowerCase();
      if (t === "checkbox") {
        this.local.defaultValue = !!this.local.defaultValue;
        this.local.placeholder = "";
      }
    },

    "local.allowMultiple"(nv) {
      if (!this.isRadio) return;
      if (nv) {
        this.local.defaultValue = Array.isArray(this.local.defaultValue)
          ? this.local.defaultValue.filter((v) => this.localOptions.includes(v))
          : this.local.defaultValue && this.localOptions.includes(this.local.defaultValue)
          ? [this.local.defaultValue]
          : [];
        if (this.local.popupLogic?.enabled && !Array.isArray(this.local.popupLogic.value)) {
          this.local.popupLogic.value =
            this.local.popupLogic.value === "" ||
            this.local.popupLogic.value === null ||
            this.local.popupLogic.value === undefined
              ? []
              : [String(this.local.popupLogic.value).trim()].filter(Boolean);
        }
      } else {
        if (Array.isArray(this.local.defaultValue)) {
          this.local.defaultValue = this.local.defaultValue[0] || "";
        }
        if (this.local.popupLogic && Array.isArray(this.local.popupLogic.value)) {
          this.local.popupLogic.value = this.local.popupLogic.value[0] || "";
        }
      }
    },

    localOptions: {
      deep: true,
      handler() {
        if (!this.isChoice) return;
        this.local.dominantOptions = normalizeDominantOptions(
          this.local.dominantOptions,
          this.localOptions
        );

        if (this.isRadio && this.local.allowMultiple) {
          if (!Array.isArray(this.local.defaultValue)) {
            this.local.defaultValue = this.local.defaultValue
              ? [String(this.local.defaultValue).trim()]
              : [];
          }

          this.local.defaultValue = this.local.defaultValue.filter((v) =>
            this.localOptions.includes(v)
          );

          if (this.local.popupLogic?.enabled) {
            if (!Array.isArray(this.local.popupLogic.value)) {
              this.local.popupLogic.value = this.local.popupLogic.value
                ? [String(this.local.popupLogic.value).trim()]
                : [];
            }

            this.local.popupLogic.value = this.local.popupLogic.value.filter((v) =>
              this.localOptions.includes(v)
            );
          }
        } else {
          if (!this.localOptions.includes(this.local.defaultValue)) {
            this.local.defaultValue = "";
          }

          if (
            this.local.popupLogic?.enabled &&
            this.local.popupLogic.value &&
            !this.localOptions.includes(this.local.popupLogic.value)
          ) {
            this.local.popupLogic.value = "";
          }
        }
        this.optionsCount = this.localOptions.length;
      },
    },
  },

  methods: {
    syncMeasurementEditor(measurement) {
      this.localMeasurementConcept = String(measurement?.concept || "");
      this.localMeasurementUnitMode =
        measurement?.unitSource === "field" && measurement?.unit
          ? `field:${measurement.unit}`
          : "study_default";
    },
    onMeasurementConceptChanged() {
      if (!this.localMeasurementConcept) {
        delete this.local.measurement;
        this.localMeasurementUnitMode = "study_default";
        return;
      }
      this.localMeasurementUnitMode = "study_default";
      this.local.measurement = {
        concept: this.localMeasurementConcept,
        unitSource: "study_default",
      };
    },
    onMeasurementUnitModeChanged() {
      if (!this.localMeasurementConcept) return;
      if (this.localMeasurementUnitMode.startsWith("field:")) {
        this.local.measurement = {
          concept: this.localMeasurementConcept,
          unitSource: "field",
          unit: this.localMeasurementUnitMode.slice(6),
        };
      } else {
        this.local.measurement = {
          concept: this.localMeasurementConcept,
          unitSource: "study_default",
        };
      }
    },
    captureInitialConstraintState() {
      this.initialConstraintEditSnapshot = constraintEditSnapshot(
        this.local,
        this.allowedFormatsText
      );
      this.initialConstraintOptions = Array.isArray(this.localOptions)
        ? [...this.localOptions]
        : [];
    },
    constraintsForCurrentSave(generated, finalOptions = null) {
      const originalType = String(this.currentFieldType || "text").toLowerCase();
      const nextType = String(this.localType || originalType).toLowerCase();
      const choiceMembershipUnchanged = this.isChoice
        ? haveSameChoiceOptions(
            this.initialConstraintOptions,
            this.localOptions
          )
        : true;

      return constraintsForSave({
        generated,
        original: this.fieldDefinition?.constraints || {},
        initialSnapshot: this.initialConstraintEditSnapshot,
        currentSnapshot: constraintEditSnapshot(
          this.local,
          this.allowedFormatsText
        ),
        sameType: nextType === originalType,
        choiceMembershipUnchanged,
        finalOptions,
      });
    },
    operatorsForType(type) {
      const t = String(type || "text").toLowerCase();

      if (t === "number" || t === "slider") {
        return [
          { value: "eq", label: "Equals" },
          { value: "neq", label: "Does not equal" },
          { value: "gt", label: "Greater than" },
          { value: "gte", label: "Greater than or equal" },
          { value: "lt", label: "Less than" },
          { value: "lte", label: "Less than or equal" },
          { value: "between", label: "Between" },
          { value: "is_empty", label: "Is empty" },
          { value: "is_not_empty", label: "Is not empty" },
        ];
      }

      if (t === "date" || t === "time") {
        return [
          { value: "eq", label: "Equals" },
          { value: "neq", label: "Does not equal" },
          { value: "gt", label: "After / Greater than" },
          { value: "gte", label: "On or after / Greater than or equal" },
          { value: "lt", label: "Before / Less than" },
          { value: "lte", label: "On or before / Less than equal" },
          { value: "between", label: "Between" },
          { value: "is_empty", label: "Is empty" },
          { value: "is_not_empty", label: "Is not empty" },
        ];
      }

      if (t === "checkbox") {
        return [
          { value: "eq", label: "Equals" },
          { value: "neq", label: "Does not equal" },
        ];
      }

      return [
        { value: "eq", label: "Equals" },
        { value: "neq", label: "Does not equal" },
        { value: "contains", label: "Contains" },
        { value: "not_contains", label: "Does not contain" },
        { value: "is_empty", label: "Is empty" },
        { value: "is_not_empty", label: "Is not empty" },
      ];
    },
    fieldsForSelectedPopupTargetSection() {
      const sectionKey = String(this.local?.popupLogic?._targetSectionKey || "");
      if (!sectionKey) return [];

      return this.availableTargetFields.filter(
        (f) => String(f.sectionKey) === sectionKey
      );
    },

    onPopupTargetToggle() {
      const popup = this.local.popupLogic;
      if (!popup) return;

      if (!popup.showInTargetField) {
        popup._targetSectionKey = "";
        popup.targetFieldKey = "";
      }
    },

    onPopupTargetSectionChanged() {
      const popup = this.local.popupLogic;
      if (!popup) return;

      const fields = this.fieldsForSelectedPopupTargetSection();

      if (!fields.length) {
        popup.targetFieldKey = "";
        return;
      }

      const stillValid = fields.some(
        (f) => String(f.key) === String(popup.targetFieldKey || "")
      );

      if (!stillValid) {
        popup.targetFieldKey = "";
      }
    },
    buildCurrentFieldSnapshot() {
      return {
        ...(this.fieldDefinition || {}),
        type: this.currentFieldType,
        placeholder: this.local?.placeholder ?? this.fieldDefinition?.placeholder ?? "",
        rows: this.fieldDefinition?.rows,
        value: this.fieldDefinition?.value,
        options: Array.isArray(this.localOptions)
          ? [...this.localOptions]
          : Array.isArray(this.fieldDefinition?.options)
          ? [...this.fieldDefinition.options]
          : [],
        constraints: {
          ...(this.fieldDefinition?.constraints || {}),
          ...(this.local || {}),
          visibilityLogic: this.cleanedVisibilityLogic(),
          popupLogic: this.cleanedPopupLogic(),
        },
      };
    },

    applyConversionPreview(report) {
      if (!report) return;

      const existingVisibilityLogic = this.local?.visibilityLogic
        ? JSON.parse(JSON.stringify(this.local.visibilityLogic))
        : {
            action: "show",
            match: "all",
            rules: [this.makeLogicRule()],
          };

      const existingPopupLogic = this.local?.popupLogic
        ? JSON.parse(JSON.stringify(this.local.popupLogic))
        : this.normalizePopupLogic(null, report.nextType || this.localType, !!this.local?.allowMultiple);

      const existingRuleSectionKeys = Array.isArray(existingVisibilityLogic.rules)
        ? existingVisibilityLogic.rules.map((r) => ({
            sourceFieldKey: String(r?.sourceFieldKey || ""),
            _sectionKey: String(r?._sectionKey || ""),
          }))
        : [];

      this.local = {
        ...this.local,
        ...(report.nextConstraints || {}),
        visibilityLogic: existingVisibilityLogic, // preserve advanced tab exactly
        popupLogic: existingPopupLogic, // preserve pop-up reminder settings during type conversion preview
      };

      this.localOptions =
        Array.isArray(report.nextOptions) && report.nextOptions.length
          ? [...report.nextOptions]
          : (report.nextType === "select" || report.nextType === "radio")
          ? ["Option 1"]
          : [];

      this.optionsCount = this.localOptions.length || 1;

      if ("nextPlaceholder" in report) {
        this.local.placeholder = report.nextPlaceholder || "";
      }

      if (report.nextType === "textarea") {
        this.local.rows = report.nextRows || this.fieldDefinition?.rows || 4;
      } else {
        delete this.local.rows;
      }

      this.$nextTick(() => {
        const rules = this.local?.visibilityLogic?.rules;
        if (!Array.isArray(rules)) return;

        rules.forEach((rule) => {
          const matched = existingRuleSectionKeys.find(
            (x) => x.sourceFieldKey === String(rule?.sourceFieldKey || "")
          );
          if (matched && matched._sectionKey) {
            rule._sectionKey = matched._sectionKey;
          }
        });
      });
    },

    onFieldTypeChange() {
      if (!this.allowTypeChange) {
        this.localType = (this.currentFieldType || "text").toLowerCase();
        return;
      }

      const nextType = String(this.localType || "").toLowerCase();
      const prevType = String(this.currentFieldType || "").toLowerCase();

      if (!nextType || nextType === prevType) {
        this.conversionReport = null;
        this.lossyConversionConfirmed = false;
        return;
      }

      const snapshot = this.buildCurrentFieldSnapshot();

      const report = getFieldTypeConversionReport({
        field: snapshot,
        fromType: prevType,
        toType: nextType,
      });

      this.conversionReport = report;
      this.lossyConversionConfirmed = false;
      this.applyConversionPreview(report);
    },
    syncRuleSectionKeys() {
      const rules = this.local?.visibilityLogic?.rules;
      if (!Array.isArray(rules)) return;

      const sourceFields = Array.isArray(this.availableSourceFields)
        ? this.availableSourceFields
        : [];

      rules.forEach((rule) => {
        if (!rule || !rule.sourceFieldKey) return;

        const matchedField =
          sourceFields.find((f) => String(f.key) === String(rule.sourceFieldKey)) || null;

        rule._sectionKey = matchedField?.sectionKey || "";

        const isMultiChoice =
          !!matchedField &&
          String(matchedField.type || "").toLowerCase() === "radio" &&
          !!matchedField.allowMultiple;

        if (isMultiChoice && !Array.isArray(rule.value)) {
          rule.value =
            rule.value === "" || rule.value === null || rule.value === undefined
              ? []
              : [String(rule.value).trim()].filter(Boolean);
        }
      });
      const popup = this.local?.popupLogic;
      if (popup?.targetFieldKey) {
          const targetFields = Array.isArray(this.availableTargetFields)
            ? this.availableTargetFields
            : [];

          const matchedTarget =
            targetFields.find((f) => String(f.key) === String(popup.targetFieldKey)) || null;

          popup._targetSectionKey = matchedTarget?.sectionKey || "";
          popup.showInTargetField = !!matchedTarget;
      }
    },
    fieldsForSelectedSection(rule) {
      const sectionKey = String(rule?._sectionKey || "");
      if (!sectionKey) return [];
      return this.availableSourceFields.filter(
        (f) => String(f.sectionKey) === sectionKey
      );
    },
    onSourceSectionChanged(rule) {
      const fields = this.fieldsForSelectedSection(rule);

      if (!fields.length) {
        rule.sourceFieldKey = "";
        rule.operator = "eq";
        rule.value = "";
        rule.valueTo = "";
        rule._chipInput = "";
        return;
      }

      const stillValid = fields.some(
        (f) => String(f.key) === String(rule.sourceFieldKey || "")
      );

      if (!stillValid) {
        rule.sourceFieldKey = "";
        rule.operator = "eq";
        rule.value = "";
        rule.valueTo = "";
        rule._chipInput = "";
      }
    },
    num(v) {
      return v === undefined || v === null || Number.isNaN(v) ? "" : v;
    },

    setNum(key, evt) {
      const raw = evt?.target?.value;
      if (raw === "" || raw === null || raw === undefined) {
        this.local[key] = undefined;
        return;
      }
      const n = Number(raw);
      this.local[key] = Number.isFinite(n) ? n : undefined;
    },

    uuidForRule() {
      return `rule_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    },

    makeLogicRule() {
      return {
        id: this.uuidForRule(),
        _sectionKey: "",
        sourceFieldKey: "",
        operator: "eq",
        value: "",
        valueTo: "",
        _chipInput: "",
      };
    },

    normalizeSingleLogicRule(rule) {
      if (!rule || typeof rule !== "object") return this.makeLogicRule();

      const sourceFieldKey = String(rule.sourceFieldKey || "");
      const sourceFields = Array.isArray(this.availableSourceFields)
        ? this.availableSourceFields
        : [];

      const matchedField =
        sourceFields.find((f) => String(f.key) === sourceFieldKey) || null;

      const isMultiChoice =
        !!matchedField &&
        String(matchedField.type || "").toLowerCase() === "radio" &&
        !!matchedField.allowMultiple;

      let value;

      if (isMultiChoice) {
        value = Array.isArray(rule.value)
          ? rule.value.map((v) => String(v ?? "").trim()).filter(Boolean)
          : rule.value === "" || rule.value === null || rule.value === undefined
          ? []
          : [String(rule.value).trim()].filter(Boolean);
      } else {
        // Important: do not collapse saved array during initial load.
        // matchedField may not be resolved yet when the dialog first initializes.
        value = Array.isArray(rule.value) ? [...rule.value] : rule.value ?? "";
      }

      return {
        id: rule.id || this.uuidForRule(),
        _sectionKey: matchedField?.sectionKey || "",
        sourceFieldKey,
        operator: String(rule.operator || "eq"),
        value,
        valueTo: rule.valueTo ?? "",
        _chipInput: "",
      };
    },

    normalizeVisibilityLogic(raw) {
      const src = raw && typeof raw === "object" ? raw : {};
      const rules =
        Array.isArray(src.rules) && src.rules.length
          ? src.rules.map((rule) => this.normalizeSingleLogicRule(rule)).filter(Boolean)
          : [this.makeLogicRule()];

      return {
        action: src.action === "hide" ? "hide" : "show",
        match: src.match === "any" ? "any" : "all",
        rules,
      };
    },

    normalizePopupLogic(raw, currentType = "text", allowMultiple = false) {
      const src = raw && typeof raw === "object" ? raw : {};
      const type = String(currentType || "text").toLowerCase();
      const isMultiChoice = type === "radio" && !!allowMultiple;

      let value;

      if (isMultiChoice) {
        value = Array.isArray(src.value)
          ? src.value.map((v) => String(v ?? "").trim()).filter(Boolean)
          : src.value === "" || src.value === null || src.value === undefined
          ? []
          : [String(src.value).trim()].filter(Boolean);
      } else if (type === "checkbox") {
        value =
          src.value === false ||
          src.value === "false" ||
          src.value === 0 ||
          src.value === "0"
            ? false
            : true;
      } else {
        value = Array.isArray(src.value) ? src.value[0] || "" : src.value ?? "";
      }

      const targetFieldKey = String(
          src.targetFieldKey ||
          src.targetFieldId ||
          src.target ||
          ""
        ).trim();

      return {
          enabled: !!src.enabled,
          operator: String(src.operator || "eq"),
          value,
          valueTo: src.valueTo ?? "",
          message: String(src.message || ""),

          showInTargetField: !!targetFieldKey || !!src.showInTargetField,
          targetFieldKey,
          _targetSectionKey: "",
          _chipInput: "",
      };
    },

    cleanedVisibilityLogic() {
      const src = this.local.visibilityLogic || {};
      const cleanedRules = (Array.isArray(src.rules) ? src.rules : [])
        .map((rule) => {
          const r = {
            sourceFieldKey: String(rule.sourceFieldKey || ""),
            operator: String(rule.operator || "eq"),
          };

          if (!r.sourceFieldKey) return null;

          if (this.needsNoValue(r.operator)) return r;

          if (r.operator === "between") {
            r.value = rule.value;
            r.valueTo = rule.valueTo;
            return r;
          }

          if (this.ruleSourceIsChoiceMulti(rule)) {
            const values = [];

            if (Array.isArray(rule.value)) {
              rule.value.forEach((v) => {
                const val = String(v ?? "").trim();
                if (val && !values.includes(val)) values.push(val);
              });
            } else {
              const val = String(rule.value ?? "").trim();
              if (val) values.push(val);
            }

            const pending = String(rule._chipInput || "").trim();
            if (pending && !values.includes(pending)) values.push(pending);

            r.value = values;
            return r;
          }

          r.value = Array.isArray(rule.value) ? rule.value[0] || "" : rule.value;
          return r;
        })
        .filter(Boolean);

      return {
        action: src.action === "hide" ? "hide" : "show",
        match: src.match === "any" ? "any" : "all",
        rules: cleanedRules,
      };
    },

    cleanedPopupLogic() {
      const src = this.local.popupLogic || {};
      const enabled = !!src.enabled;
      const operator = String(src.operator || "eq");

      if (!enabled) {
        return {
          enabled: false,
          operator: "eq",
          value: "",
          valueTo: "",
          message: "",
          showInTargetField: false,
          targetFieldKey: "",
        };
      }

      const cleaned = {
        enabled: true,
        operator,
        message: String(src.message || "").trim(),
      };
      const targetFieldKey = String(src.targetFieldKey || "").trim();

      cleaned.showInTargetField = !!src.showInTargetField && !!targetFieldKey;
      cleaned.targetFieldKey = cleaned.showInTargetField ? targetFieldKey : "";
      if (this.popupNeedsNoValue(operator)) {
        cleaned.value = "";
        cleaned.valueTo = "";
        return cleaned;
      }

      if (operator === "between") {
        cleaned.value = src.value;
        cleaned.valueTo = src.valueTo;
        return cleaned;
      }

      if (this.popupSourceIsChoiceMulti()) {
        const values = [];

        if (Array.isArray(src.value)) {
          src.value.forEach((v) => {
            const val = String(v ?? "").trim();
            if (val && !values.includes(val)) values.push(val);
          });
        } else {
          const val = String(src.value ?? "").trim();
          if (val) values.push(val);
        }

        const pending = String(src._chipInput || "").trim();
        if (pending && !values.includes(pending)) values.push(pending);

        cleaned.value = values;
        cleaned.valueTo = "";
        return cleaned;
      }

      cleaned.value = Array.isArray(src.value) ? src.value[0] || "" : src.value;
      cleaned.valueTo = "";
      return cleaned;
    },

    addLogicRule() {
      if (!Array.isArray(this.local.visibilityLogic.rules)) {
        this.local.visibilityLogic.rules = [];
      }
      this.local.visibilityLogic.rules.push(this.makeLogicRule());
    },

    removeLogicRule(idx) {
      if (!Array.isArray(this.local.visibilityLogic.rules)) {
        this.local.visibilityLogic.rules = [this.makeLogicRule()];
        return;
      }

      this.local.visibilityLogic.rules.splice(idx, 1);

      if (!this.local.visibilityLogic.rules.length) {
        this.local.visibilityLogic.rules.push(this.makeLogicRule());
      }
    },

    clearLogicRule(rule) {
      if (!rule) return;
      rule.sourceFieldKey = "";
      rule.operator = "eq";
      rule.value = "";
      rule.valueTo = "";
      rule._chipInput = "";
    },

    findSourceField(rule) {
      const key = String(rule?.sourceFieldKey || "");
      if (!key) return null;
      return this.availableSourceFields.find((f) => String(f.key) === key) || null;
    },

    ruleSourceType(rule) {
      return this.findSourceField(rule)?.type || "text";
    },

    ruleSourceIsChoiceSingle(rule) {
      const f = this.findSourceField(rule);
      return !!f && (f.type === "select" || f.type === "radio") && !f.allowMultiple;
    },

    ruleSourceIsChoiceMulti(rule) {
      const f = this.findSourceField(rule);
      return !!f && f.type === "radio" && !!f.allowMultiple;
    },

    popupSourceType() {
      return this.type || "text";
    },

    popupSourceIsChoiceSingle() {
      return (this.isSelect || this.isRadio) && !this.local.allowMultiple;
    },

    popupSourceIsChoiceMulti() {
      return this.isRadio && !!this.local.allowMultiple;
    },

    popupNeedsNoValue(operator) {
      return this.needsNoValue(operator);
    },

    sourceOptions(rule) {
      const f = this.findSourceField(rule);
      return Array.isArray(f?.options) ? f.options : [];
    },

    sourceDateFormat(rule) {
      return this.findSourceField(rule)?.dateFormat || "dd.MM.yyyy";
    },

    sourceHourCycle(rule) {
      return this.findSourceField(rule)?.hourCycle || "24";
    },

    needsNoValue(operator) {
      return ["is_empty", "is_not_empty"].includes(String(operator || ""));
    },

    availableOperatorsForRule(rule) {
      return this.operatorsForType(this.ruleSourceType(rule));
    },

    availablePopupOperatorsForCurrentField() {
      return this.operatorsForType(this.popupSourceType());
    },

    onSourceFieldChanged(rule) {
      const ops = this.availableOperatorsForRule(rule).map((o) => o.value);
      if (!ops.includes(rule.operator)) {
        rule.operator = ops[0] || "eq";
      }

      if (this.ruleSourceIsChoiceMulti(rule)) {
        rule.value = Array.isArray(rule.value) ? rule.value : [];
      } else if (this.ruleSourceType(rule) === "checkbox") {
        rule.value = true;
      } else {
        rule.value = "";
      }

      rule.valueTo = "";
      rule._chipInput = "";
    },

    onOperatorChanged(rule) {
      if (this.needsNoValue(rule.operator)) {
        rule.value = "";
        rule.valueTo = "";
        return;
      }

      if (rule.operator === "between") {
        rule.value = "";
        rule.valueTo = "";
        return;
      }

      if (this.ruleSourceIsChoiceMulti(rule)) {
        rule.value = Array.isArray(rule.value) ? rule.value : [];
      } else if (this.ruleSourceType(rule) === "checkbox") {
        rule.value = true;
      } else {
        rule.valueTo = "";
        if (Array.isArray(rule.value)) rule.value = rule.value[0] || "";
      }
    },

    onPopupOperatorChanged() {
      const popup = this.local.popupLogic;
      if (!popup) return;

      if (this.popupNeedsNoValue(popup.operator)) {
        popup.value = "";
        popup.valueTo = "";
        return;
      }

      if (popup.operator === "between") {
        popup.value = "";
        popup.valueTo = "";
        return;
      }

      if (this.popupSourceIsChoiceMulti()) {
        popup.value = Array.isArray(popup.value) ? popup.value : [];
      } else if (this.popupSourceType() === "checkbox") {
        popup.value = true;
      } else {
        popup.valueTo = "";
        if (Array.isArray(popup.value)) popup.value = popup.value[0] || "";
      }
    },

    addRuleChip(rule) {
      const val = String(rule?._chipInput || "").trim();
      if (!val) return;

      const allowed = this.sourceOptions(rule);
      if (allowed.length && !allowed.includes(val)) return;

      if (!Array.isArray(rule.value)) rule.value = [];
      if (!rule.value.includes(val)) rule.value.push(val);

      rule._chipInput = "";
    },

    removeRuleChip(rule, idx) {
      if (!Array.isArray(rule?.value)) return;
      rule.value.splice(idx, 1);
    },

    addPopupChip() {
      const popup = this.local.popupLogic;
      const val = String(popup?._chipInput || "").trim();
      if (!val) return;

      const allowed = Array.isArray(this.localOptions) ? this.localOptions : [];
      if (allowed.length && !allowed.includes(val)) return;

      if (!Array.isArray(popup.value)) popup.value = [];
      if (!popup.value.includes(val)) popup.value.push(val);

      popup._chipInput = "";
    },

    removePopupChip(idx) {
      const popup = this.local.popupLogic;
      if (!Array.isArray(popup?.value)) return;
      popup.value.splice(idx, 1);
    },

    logicSummary(rule) {
      const src = this.findSourceField(rule);
      if (!src || !rule?.sourceFieldKey) return "Select a source field.";

      const fieldName = src.pathLabel || src.label || src.key;
      const op = rule.operator || "eq";

      if (this.needsNoValue(op)) {
        return `${fieldName} ${op.replaceAll("_", " ")}`;
      }

      if (op === "between") {
        return `${fieldName} between ${rule.value ?? ""} and ${rule.valueTo ?? ""}`;
      }

      if (Array.isArray(rule.value)) {
        return `${fieldName} ${op} [${rule.value.join(", ")}]`;
      }

      return `${fieldName} ${op} ${rule.value ?? ""}`;
    },

    popupSummary() {
      const popup = this.local.popupLogic || {};
      if (!popup.enabled) return "Pop-up message is disabled.";

      const fieldName =
        this.currentFieldLabel ||
        this.fieldDefinition?.label ||
        this.fieldDefinition?.name ||
        "Current field";

      const op = popup.operator || "eq";

      const targetText = (() => {
        const key = String(popup.targetFieldKey || "");
        if (!popup.showInTargetField || !key) return "";

        const target = this.availableTargetFields.find((f) => String(f.key) === key);
        if (!target) return "";

        return ` Message will also be shown under target field: ${target.pathLabel || target.label}.`;
      })();

      if (this.popupNeedsNoValue(op)) {
        return `${fieldName} ${op.replaceAll("_", " ")} → show message.${targetText}`;
      }

      if (op === "between") {
        return `${fieldName} between ${popup.value ?? ""} and ${popup.valueTo ?? ""} → show message.${targetText}`;
      }

      if (Array.isArray(popup.value)) {
        return `${fieldName} ${op} [${popup.value.join(", ")}] → show message.${targetText}`;
      }

      return `${fieldName} ${op} ${popup.value ?? ""} → show message.${targetText}`;
    },

    addOrUpdateMark(kind) {
      if (kind !== "slider") return;
      let v = Number(this.markEditValue);
      const lbl = String(this.markEditLabel || "").trim();
      if (!Number.isFinite(v) || !lbl) return;

      const min = this.useMin;
      const max = this.useMax;
      const step = this.useStep;

      v = Math.max(min, Math.min(max, v));
      v = Math.round((v - min) / step) * step + min;
      v = Math.max(min, Math.min(max, v));

      const i = (this.local.marks || []).findIndex((m) => m.value === v);
      if (i >= 0) this.local.marks.splice(i, 1, { value: v, label: lbl });
      else (this.local.marks || (this.local.marks = [])).push({ value: v, label: lbl });

      this.markEditLabel = "";
      this.markEditValue = null;
    },

    removeMark(v) {
      const i = (this.local.marks || []).findIndex((m) => m.value === v);
      if (i >= 0) this.local.marks.splice(i, 1);
    },

    addChip() {
      const v = (this.chipInput || "").trim();
      if (!v) return;
      if (!this.localOptions.includes(v)) return;
      if (!Array.isArray(this.local.defaultValue)) this.local.defaultValue = [];
      if (!this.local.defaultValue.includes(v)) this.local.defaultValue.push(v);
      this.chipInput = "";
    },

    removeChip(i) {
      if (!Array.isArray(this.local.defaultValue)) return;
      this.local.defaultValue.splice(i, 1);
    },

    syncOptionsCount() {
      const count = Math.max(1, Number(this.optionsCount || 1));
      if (count > this.localOptions.length) {
        const add = count - this.localOptions.length;
        for (let i = 0; i < add; i++) {
          this.localOptions.push(`Option ${this.localOptions.length + 1}`);
        }
      } else if (count < this.localOptions.length) {
        this.localOptions.splice(count);
      }
      this.optionsCount = this.localOptions.length;
    },

    addOption() {
      this.localOptions.push(`Option ${this.localOptions.length + 1}`);
      this.optionsCount = this.localOptions.length;
    },
    moveOption(idx, direction) {
      const target = idx + direction;
      if (
        !Number.isInteger(idx) ||
        !Number.isInteger(direction) ||
        target < 0 ||
        target >= this.localOptions.length
      ) {
        return;
      }

      const option = this.localOptions.splice(idx, 1)[0];
      this.localOptions.splice(target, 0, option);
    },
    renameOption(idx, nextValue) {
      const previousValue = this.localOptions[idx];
      this.localOptions[idx] = nextValue;

      if (
        Array.isArray(this.local.dominantOptions) &&
        this.local.dominantOptions.includes(previousValue)
      ) {
        this.local.dominantOptions = this.local.dominantOptions.map((value) =>
          value === previousValue ? nextValue : value
        );
      }
    },
    isDominantOption(option) {
      return (
        Array.isArray(this.local.dominantOptions) &&
        this.local.dominantOptions.includes(option)
      );
    },
    setDominantOption(option, enabled) {
      const selected = new Set(this.local.dominantOptions || []);
      if (enabled) selected.add(option);
      else selected.delete(option);
      this.local.dominantOptions = Array.from(selected);

      if (enabled && Array.isArray(this.local.defaultValue)) {
        this.local.defaultValue = this.local.defaultValue.includes(option)
          ? [option]
          : this.local.defaultValue.filter(
              (value) => !this.local.dominantOptions.includes(value)
            );
      }
    },
    compareOptionLabels(a, b) {
      return String(a ?? "").trim().localeCompare(
        String(b ?? "").trim(),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );
    },

    sortChoiceOptions() {
      if (!Array.isArray(this.localOptions) || this.localOptions.length <= 1) return;

      const dir = this.optionSortDirection === "asc" ? 1 : -1;

      const sorted = [...this.localOptions].sort((a, b) => {
        return this.compareOptionLabels(a, b) * dir;
      });

      this.localOptions.splice(0, this.localOptions.length, ...sorted);
      this.optionSortDirection = this.optionSortDirection === "asc" ? "desc" : "asc";
    },

    removeLastOption() {
      if (this.localOptions.length > 1) {
        const removed = this.localOptions.pop();
        if (Array.isArray(this.local.defaultValue)) {
          this.local.defaultValue = this.local.defaultValue.filter((v) => v !== removed);
        } else if (this.local.defaultValue === removed) {
          this.local.defaultValue = "";
        }

        if (this.local.popupLogic?.enabled) {
          if (Array.isArray(this.local.popupLogic.value)) {
            this.local.popupLogic.value = this.local.popupLogic.value.filter((v) => v !== removed);
          } else if (this.local.popupLogic.value === removed) {
            this.local.popupLogic.value = "";
          }
        }

        this.optionsCount = this.localOptions.length;
      }
    },

    deleteOption(idx) {
      if (this.localOptions.length <= 1) return;
      const removed = this.localOptions.splice(idx, 1)[0];
      if (Array.isArray(this.local.defaultValue)) {
        this.local.defaultValue = this.local.defaultValue.filter((v) => v !== removed);
      } else if (this.local.defaultValue === removed) {
        this.local.defaultValue = "";
      }

      if (this.local.popupLogic?.enabled) {
        if (Array.isArray(this.local.popupLogic.value)) {
          this.local.popupLogic.value = this.local.popupLogic.value.filter((v) => v !== removed);
        } else if (this.local.popupLogic.value === removed) {
          this.local.popupLogic.value = "";
        }
      }

      this.optionsCount = this.localOptions.length;
    },

    addCustomMod() {
      const v = (this.customMod || "").trim();
      if (!v) return;
      const exists = (this.local.modalities || []).some(
        (m) => (m || "").toLowerCase() === v.toLowerCase()
      );
      if (!exists) {
        if (!Array.isArray(this.local.modalities)) this.local.modalities = [];
        this.local.modalities.push(v);
      }
      this.customMod = "";
    },

    removeCustomModByName(name) {
      this.local.modalities = (this.local.modalities || []).filter((m) => m !== name);
    },

    save() {
      if (this.isSaveDisabled) return;
      if (this.conversionReport?.lossy && !this.lossyConversionConfirmed) return;

      const visibilityLogic = this.cleanedVisibilityLogic();
      const popupLogic = this.cleanedPopupLogic();
      const nextType = String(this.localType || this.currentFieldType || "text").toLowerCase();
      const originalType = String(this.currentFieldType || "text").toLowerCase();
      if (!this.allowTypeChange) {
      this.localType = originalType;
    }
      const snapshot = this.buildCurrentFieldSnapshot();

      let fieldPayload;

      if (nextType !== originalType) {
        const converted = buildConvertedField({
          field: snapshot,
          fromType: originalType,
          toType: nextType,
        });

        fieldPayload = converted.field;
        fieldPayload.constraints = {
          ...(fieldPayload.constraints || {}),
          ...(this.local || {}),
          visibilityLogic,
          popupLogic,
        };

        if (this.isChoice) {
          const opts = this.localOptions.map((o) => String(o || "").trim()).filter(Boolean);
          fieldPayload.options = opts.length ? Array.from(new Set(opts)) : ["Option 1"];
          fieldPayload.constraints.dominantOptions =
            nextType === "radio" && fieldPayload.constraints.allowMultiple
              ? normalizeDominantOptions(
                  this.local.dominantOptions,
                  fieldPayload.options
                )
              : [];
        }

        if (nextType === "select") {
          delete fieldPayload.constraints.allowMultiple;
          delete fieldPayload.constraints.dominantOptions;
        }

        this.$emit("updateConstraints", {
          type: fieldPayload.type,
          field: fieldPayload,
          changedType: true,
          conversionWarnings: this.conversionReport?.warnings || [],
        });
        return;
      }
      if (this.isTable) {
      const cleaned = {
        helpText: this.local.helpText || "",
        required: !!this.local.required,
        readonly: !!this.local.readonly,
        visibilityLogic,
        popupLogic,
      };
      const savedConstraints = this.constraintsForCurrentSave(cleaned);

      this.$emit("updateConstraints", {
        type: nextType,
        field: {
          ...this.fieldDefinition,
          type: nextType,
          constraints: savedConstraints,
        },
        changedType: false,
        conversionWarnings: [],
      });
      return;
    }

      if (this.isFile) {
        const parsedFormats = String(this.allowedFormatsText || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        const cleaned = {
          required: !!this.local.required,
          readonly: !!this.local.readonly,
          helpText: this.local.helpText || "",
          allowedFormats: parsedFormats,
          maxSizeMB:
            Number.isFinite(this.local.maxSizeMB) && this.local.maxSizeMB > 0
              ? Number(this.local.maxSizeMB)
              : undefined,
          storagePreference: this.local.storagePreference === "url" ? "url" : "local",
          allowMultipleFiles: this.local.allowMultipleFiles !== false,
          modalities: Array.isArray(this.local.modalities)
            ? Array.from(new Set(this.local.modalities.filter(Boolean).map(String)))
            : [],
          visibilityLogic,
          popupLogic,
        };
        const savedConstraints = this.constraintsForCurrentSave(cleaned);

        this.$emit("updateConstraints", {
          type: nextType,
          field: {
            ...this.fieldDefinition,
            type: nextType,
            constraints: savedConstraints,
          },
          changedType: false,
          conversionWarnings: [],
        });
        return;
      }

      if (!this.isSlider) {
        const cleaned = normalizeConstraints(this.type, { ...this.local });

        let finalOptions = Array.isArray(this.fieldDefinition?.options)
          ? [...this.fieldDefinition.options]
          : [];

        if (this.isChoice) {
          const opts = this.localOptions.map((o) => String(o || "").trim()).filter(Boolean);
          finalOptions = opts.length ? Array.from(new Set(opts)) : ["Option 1"];

          if (this.isRadio && this.local.allowMultiple) {
            cleaned.dominantOptions = normalizeDominantOptions(
              this.local.dominantOptions,
              finalOptions
            );
            const arr = Array.isArray(cleaned.defaultValue) ? cleaned.defaultValue : [];
            cleaned.defaultValue = normalizeMultiChoiceValue(
              arr,
              finalOptions,
              cleaned.dominantOptions
            );
          } else {
            if (!finalOptions.includes(cleaned.defaultValue)) cleaned.defaultValue = "";
          }

          cleaned.options = finalOptions;
          if (this.isSelect) {
            delete cleaned.allowMultiple;
            delete cleaned.dominantOptions;
          }
        }

        if (this.isRadio && this.local.allowMultiple) {
          cleaned.defaultValue = Array.isArray(cleaned.defaultValue)
            ? normalizeMultiChoiceValue(
                cleaned.defaultValue
                  .map((v) => String(v ?? "").trim())
                  .filter(Boolean),
                finalOptions,
                cleaned.dominantOptions
              )
            : cleaned.defaultValue
            ? [String(cleaned.defaultValue).trim()].filter((v) => v && finalOptions.includes(v))
            : [];
        } else {
          cleaned.defaultValue = coerceDefaultForType(this.type, cleaned.defaultValue);
        }
        cleaned.hourCycle = this.local.hourCycle || "24";
        cleaned.visibilityLogic = visibilityLogic;
        cleaned.popupLogic = popupLogic;
        const savedConstraints = this.constraintsForCurrentSave(
          cleaned,
          this.isChoice ? finalOptions : null
        );

        const updatedField = {
          ...this.fieldDefinition,
          type: nextType,
          constraints: savedConstraints,
          placeholder:
            Object.prototype.hasOwnProperty.call(savedConstraints, "placeholder")
              ? savedConstraints.placeholder || ""
              : this.fieldDefinition?.placeholder || "",
          options: this.isChoice ? finalOptions : [],
        };

        this.$emit("updateConstraints", {
          type: nextType,
          field: updatedField,
          changedType: false,
          conversionWarnings: [],
        });
        return;
      }

      if (this.local.mode === "slider") {
        const min = this.local.percent ? 1 : this.useMin;
        const max = this.local.percent ? 100 : this.useMax;
        const step = this.local.percent ? 1 : this.useStep;

        const marks = (this.local.marks || [])
          .map((m) => {
            let v = Number(m.value);
            if (!Number.isFinite(v)) return null;
            v = Math.round((v - min) / step) * step + min;
            v = Math.max(min, Math.min(max, v));
            return { value: v, label: String(m.label || "") };
          })
          .filter((m) => m && m.label)
          .filter((m, idx, arr) => arr.findIndex((x) => x.value === m.value) === idx)
          .sort((a, b) => a.value - b.value);

        const cleaned = {
          mode: "slider",
          required: !!this.local.required,
          readonly: !!this.local.readonly,
          helpText: this.local.helpText || "",
          percent: !!this.local.percent,
          min,
          max,
          step,
          marks,
          visibilityLogic,
          popupLogic,
        };
        const savedConstraints = this.constraintsForCurrentSave(cleaned);

        this.$emit("updateConstraints", {
          type: nextType,
          field: {
            ...this.fieldDefinition,
            type: nextType,
            constraints: savedConstraints,
          },
          changedType: false,
          conversionWarnings: [],
        });
        return;
      }

      const LIM = this.LINEAR_MAX;
      let linMin = this.useMin;
      let linMax = this.useMax;
      if (linMax - linMin + 1 > LIM) linMax = linMin + (LIM - 1);
      if (linMax < linMin) linMax = linMin + 1;

      const cleaned = {
        mode: "linear",
        required: !!this.local.required,
        readonly: !!this.local.readonly,
        helpText: this.local.helpText || "",
        min: linMin,
        max: linMax,
        leftLabel: this.local.leftLabel || "",
        rightLabel: this.local.rightLabel || "",
        visibilityLogic,
        popupLogic,
      };
      const savedConstraints = this.constraintsForCurrentSave(cleaned);

      this.$emit("updateConstraints", {
        type: nextType,
        field: {
          ...this.fieldDefinition,
          type: nextType,
          constraints: savedConstraints,
        },
        changedType: false,
        conversionWarnings: [],
      });
    },

    clearToInitial() {
      this.conversionReport = null;
      this.lossyConversionConfirmed = false;
      const t = this.type;

      this.local.required = false;
      this.local.readonly = false;
      this.local.helpText = "";
      if (!this.isCheckbox && !(this.isSlider && this.local.mode === "slider") && !this.isFile) {
        this.local.placeholder = "";
      }

      if (t === "checkbox") this.local.defaultValue = false;
      else if (t === "radio" && this.local.allowMultiple) this.local.defaultValue = [];
      else this.local.defaultValue = "";
      if (t === "table") {
      this.local.placeholder = "";
      this.local.defaultValue = undefined;
      this.local.visibilityLogic = {
        action: "show",
        match: "all",
        rules: [this.makeLogicRule()],
      };
      this.local.popupLogic = this.normalizePopupLogic(null, t, !!this.local.allowMultiple);

      this.activeTab = "basic";
      this.chipInput = "";
      this.markEditValue = null;
      this.markEditLabel = "";
      this.customMod = "";
      return;
    }
      if (t === "number") {
        this.local.min = undefined;
        this.local.max = undefined;
        this.local.step = undefined;
        this.local.integerOnly = false;
        this.local.minDigits = undefined;
        this.local.maxDigits = undefined;
        delete this.local.measurement;
        this.syncMeasurementEditor(null);
      } else if (t === "text" || t === "textarea") {
        this.local.minLength = undefined;
        this.local.maxLength = undefined;
        this.local.pattern = "";
        this.local.transform = "none";
      } else if (t === "time") {
        this.local.minTime = "";
        this.local.maxTime = "";
      } else if (t === "date") {
        this.local.minDate = "";
        this.local.maxDate = "";
        this.local.defaultValue = "";
      } else if (t === "slider") {
        this.local.percent = false;
        this.local.marks = [];
        if (this.local.mode === "linear") {
          this.local.min = 1;
          this.local.max = 5;
          this.local.leftLabel = "";
          this.local.rightLabel = "";
        } else {
          this.local.min = 1;
          this.local.max = 5;
          this.local.step = 1;
        }
      } else if (t === "file") {
        this.allowedFormatsText = "";
        this.local.allowedFormats = [];
        this.local.maxSizeMB = undefined;
        this.local.storagePreference = "local";
        this.local.allowMultipleFiles = true;
        this.local.modalities = [];
      } else if (t === "radio" || t === "select") {
        this.local.defaultValue = this.local.allowMultiple ? [] : "";
      }

      this.local.visibilityLogic = {
        action: "show",
        match: "all",
        rules: [this.makeLogicRule()],
      };
      this.local.popupLogic = this.normalizePopupLogic(null, t, !!this.local.allowMultiple);

      this.activeTab = "basic";
      this.chipInput = "";
      this.markEditValue = null;
      this.markEditLabel = "";
      this.customMod = "";
    },
  },
};
</script>

<style scoped>
.constraints-edit-modal {
  width: 600px;
  background: #fff;
  padding: 18px 18px 14px;
  border-radius: 14px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.22);
  max-height: 82vh;
  overflow-y: auto;
  box-sizing: border-box;
}
.head {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f7;
}

.head h3 {
  margin: 0;
  color: #111827;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
}

.dialog-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin: 0 0 14px;
  padding: 4px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}
.dialog-tab {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  transition:
    background 0.18s ease,
    color 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.18s ease;
}
.dialog-tab:hover {
  background: #fff;
  color: #111827;
}
.dialog-tab.active {
  background: #fff;
  color: #1d4ed8;
  border-color: #bfdbfe;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.14);
}

.group {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-top: 10px;
}
.row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}
.row.two {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}
.choice-row {
  display: flex;
  gap: 16px;
}
label {
  font-size: 12px;
  color: #374151;
}
input[type="text"],
input[type="number"],
input[type="time"],
select {
  width: 100%;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
.chk {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.note {
  color: #6b7280;
  font-size: 12px;
}
.options-scroll {
  max-height: 220px;
  overflow: auto;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  padding: 8px;
}
.opt-row {
  display: grid;
  grid-template-columns: 24px minmax(140px, 1fr) auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.opt-row.has-exclusive-control {
  grid-template-columns: 24px minmax(140px, 1fr) auto auto;
}
.dominant-option {
  white-space: nowrap;
}
.option-actions {
  display: inline-flex;
  gap: 4px;
}
.opt-index {
  text-align: right;
  color: #6b7280;
  font-size: 12px;
}
.icon-btn {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  cursor: pointer;
  padding: 4px 8px;
}
.icon-btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}
.quick {
  display: flex;
  gap: 6px;
}
.chips {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.chip-input-row input {
  width: 100%;
}
.add-mod-row {
  display: grid;
  grid-template-columns: 1fr 40px;
  gap: 6px;
}
.add-mod-btn {
  padding: 8px;
  border-radius: 6px;
}
.chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.chip {
  background: #eef2ff;
  color: #111827;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.chip.selectable input {
  margin-right: 6px;
}
.chip-x {
  background: transparent;
  border: none;
  cursor: pointer;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
.btn-primary {
  background: #2563eb;
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s ease, opacity 0.2s ease;
}
.btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}
.btn-primary:disabled {
  background: #93c5fd;
  cursor: not-allowed;
  opacity: 0.7;
}
.btn-option {
  background: #e5e7eb;
  color: #111827;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
}
.btn-mini {
  background: #eef2ff;
  color: #1f2937;
  border: 1px solid #c7d2fe;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}
.err {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

.logic-rules {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}
.logic-rule-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  background: #fafafa;
}
.logic-rule-card select {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}
.logic-rule-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.logic-rule-top-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.conversion-warning-box {
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #9a3412;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
}

.conversion-warning-box ul {
  margin: 0;
  padding-left: 18px;
}

.conversion-confirm {
  margin-top: 8px;
}

.sort-option-btn {
  min-width: 76px;
}


.sort-option-btn:disabled,
.sort-mark-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.field-settings-title-wrap {
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.field-settings-current-field {
  max-width: 100%;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.45;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-settings-current-field strong {
  color: #111827;
  font-weight: 800;
}

.field-settings-type-inline {
  margin-left: 10px;
  color: #6b7280;
}

.field-settings-type-inline strong {
  color: #111827;
  font-weight: 800;
}
</style>
